import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mockSales } from '@/mocks/tr/sales';
import { mockCustomers } from '@/mocks/tr/customers';
import { mockSuppliers } from '@/mocks/tr/suppliers';
import { mockExpenses } from '@/mocks/tr/expenses';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
}

export default function Cash() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('today');

  const stats = useMemo(() => {
    const totalSales = mockSales.reduce((sum, s) => sum + s.total_amount, 0);
    const totalCollection = mockSales.reduce((sum, s) => sum + s.paid_amount, 0);
    const totalExpenses = mockExpenses.reduce((sum, e) => sum + e.amount, 0);
    const customerReceivable = mockCustomers.reduce((sum, c) => sum + c.remaining_debt, 0);
    const supplierPayable = mockSuppliers.reduce((sum, s) => sum + s.remaining_debt, 0);
    const pendingExpenses = mockExpenses.filter((e) => e.payment_status === 'Ödenecek').reduce((sum, e) => sum + e.amount, 0);
    const netStatus = totalCollection - totalExpenses;
    return { totalSales, totalCollection, totalExpenses, customerReceivable, supplierPayable, pendingExpenses, netStatus };
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-boutique-black">{t('cash.title')}</h1>
          <p className="text-sm text-boutique-warm mt-1">Genel finans özetini görüntüleyin</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 bg-white p-1 rounded-xl border border-cream-200 inline-flex">
        {[
          { key: 'today', label: t('common.today') },
          { key: 'week', label: t('common.thisWeek') },
          { key: 'month', label: t('common.thisMonth') },
          { key: 'custom', label: t('common.custom') },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => setFilter(item.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              filter === item.key
                ? 'bg-boutique-black text-white'
                : 'text-boutique-warm hover:text-boutique-black hover:bg-cream-100'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-cream-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gold-50 flex items-center justify-center text-gold-500">
              <i className="ri-shopping-bag-line text-xl" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">{t('cash.totalSales')}</p>
              <p className="text-2xl font-semibold text-boutique-black">{formatCurrency(stats.totalSales)}</p>
            </div>
          </div>
          <div className="h-1.5 bg-cream-100 rounded-full overflow-hidden">
            <div className="h-full bg-gold-400 rounded-full" style={{ width: '75%' }} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-cream-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center text-green-500">
              <i className="ri-money-cny-circle-line text-xl" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">{t('cash.totalCollection')}</p>
              <p className="text-2xl font-semibold text-green-600">{formatCurrency(stats.totalCollection)}</p>
            </div>
          </div>
          <div className="h-1.5 bg-cream-100 rounded-full overflow-hidden">
            <div className="h-full bg-green-400 rounded-full" style={{ width: '60%' }} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-cream-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500">
              <i className="ri-bill-line text-xl" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">{t('cash.totalExpenses')}</p>
              <p className="text-2xl font-semibold text-red-600">{formatCurrency(stats.totalExpenses)}</p>
            </div>
          </div>
          <div className="h-1.5 bg-cream-100 rounded-full overflow-hidden">
            <div className="h-full bg-red-400 rounded-full" style={{ width: '45%' }} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-cream-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center text-rose-500">
              <i className="ri-time-line text-xl" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">{t('cash.customerReceivable')}</p>
              <p className="text-2xl font-semibold text-rose-600">{formatCurrency(stats.customerReceivable)}</p>
            </div>
          </div>
          <div className="h-1.5 bg-cream-100 rounded-full overflow-hidden">
            <div className="h-full bg-rose-400 rounded-full" style={{ width: '30%' }} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-cream-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-500">
              <i className="ri-truck-line text-xl" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">{t('cash.supplierPayable')}</p>
              <p className="text-2xl font-semibold text-blue-600">{formatCurrency(stats.supplierPayable)}</p>
            </div>
          </div>
          <div className="h-1.5 bg-cream-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-400 rounded-full" style={{ width: '55%' }} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-cream-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center text-purple-500">
              <i className="ri-safe-2-line text-xl" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">{t('cash.pendingExpenses')}</p>
              <p className="text-2xl font-semibold text-purple-600">{formatCurrency(stats.pendingExpenses)}</p>
            </div>
          </div>
          <div className="h-1.5 bg-cream-100 rounded-full overflow-hidden">
            <div className="h-full bg-purple-400 rounded-full" style={{ width: '20%' }} />
          </div>
        </div>
      </div>

      {/* Net Status */}
      <div className={`rounded-xl border p-6 ${stats.netStatus >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${stats.netStatus >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
              <i className="ri-bar-chart-line text-2xl" />
            </div>
            <div>
              <p className="text-sm font-medium text-boutique-warm uppercase">{t('cash.netStatus')}</p>
              <p className={`text-3xl font-semibold ${stats.netStatus >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(stats.netStatus)}
              </p>
            </div>
          </div>
          <div className={`text-sm font-medium px-3 py-1 rounded-full ${stats.netStatus >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {stats.netStatus >= 0 ? 'Kâr' : 'Zarar'}
          </div>
        </div>
      </div>
    </div>
  );
}